/* eslint-disable no-console */
import { ref } from "@vue/composition-api";
import { Communicator } from "@pw2016/pw-player-communicator";
import { connectionStateEnum } from "@/const";

export interface PWPlayerState {
  connecting: boolean;
  connectingState: connectionStateEnum;
}

export interface UsePWPlayerCommunicatorOptions {
  sessionToken: string;
  onVideoPlay?: () => void;
  onVideoStop?: () => void;
  onVideoOffline?: () => void;
  onVideoError?: () => void;
  onChatMessage?: (data: { nick: string; message: string }) => void;
  onRoomModeUpdate?: (roomMode: string) => void;
  onVIPRequestStatusUpdate?: (status: string) => void;
  onUserCamStatus?: (status: string) => void;
  onDisconnected?: (reason: string) => void;
}

//TODO: remove unused methods and listeners
export default function usePWPlayerCommunicator(options: UsePWPlayerCommunicatorOptions) {
  const communicator = ref<any>(null);
  const state: PWPlayerState = {
    connecting: false,
    connectingState: connectionStateEnum.EMPTY,
  };

  const onVideoPlay = () => {
    console.debug("videoPlay");
    state.connecting = false;
    state.connectingState = connectionStateEnum.CONNECTED;
    options.onVideoPlay?.();
  };

  const onVideoStop = () => {
    console.debug("videoStop");
    options.onVideoStop?.();
  };

  const onVideoOffline = () => {
    console.debug("videoOffline");
    state.connectingState = connectionStateEnum.DISCONNECTED;
    options.onVideoOffline?.();
  };

  const onVideoError = () => {
    console.debug("videoError");
    state.connectingState = connectionStateEnum.DISCONNECTED;
    options.onVideoError?.();
  };

  const onChatMessage = (data: { nick: string; message: string }) => {
    options.onChatMessage?.(data);
  };

  const onRoomModeUpdate = (roomMode: string) => {
    console.debug("roomModeUpdate", roomMode);
    options.onRoomModeUpdate?.(roomMode);
  };

  const onVIPRequestStatusUpdate = (status: string) => {
    console.debug("roomModeUpdate", status);
    options.onVIPRequestStatusUpdate?.(status);
  };

  const onUserCamStatus = (status: string) => {
    console.debug("userCamStatus", status);
    options.onUserCamStatus?.(status);
  };

  const onDisconnected = (reason: string) => {
    console.debug("disconnected", reason);
    state.connecting = false;
    state.connectingState = connectionStateEnum.DISCONNECTED;
    options.onDisconnected?.(reason);
  };

  const cleanup = () => {
    if (communicator.value) {
      communicator.value.off("videoPlay", onVideoPlay);
      communicator.value.off("videoStop", onVideoStop);
      communicator.value.off("videoOffline", onVideoOffline);
      communicator.value.off("videoError", onVideoError);
      communicator.value.off("chatMessage", onChatMessage);
      communicator.value.off("roomModeUpdate", onRoomModeUpdate);
      communicator.value.off("VIPRequestStatusUpdate", onVIPRequestStatusUpdate);
      communicator.value.off("userCamStatus", onUserCamStatus);
      communicator.value.off("disconnected", onDisconnected);
      communicator.value = null;
    }
  };

  const initCommunicator = () => {
    if (!communicator.value) {
      cleanup();
    }
    if (!options.sessionToken) {
      console.error("[PW Player] Cannot initialize communicator without session token");
      return;
    }

    state.connecting = true;
    state.connectingState = connectionStateEnum.CONNECTING;
    communicator.value = new Communicator({
      sessionToken: options.sessionToken,
    });

    communicator.value.on("videoPlay", onVideoPlay);
    communicator.value.on("videoStop", onVideoStop);
    communicator.value.on("videoOffline", onVideoOffline);
    communicator.value.on("videoError", onVideoError);
    // Chat events
    communicator.value.on("chatMessage", onChatMessage);
    communicator.value.on("roomModeUpdate", onRoomModeUpdate);
    communicator.value.on("VIPRequestStatusUpdate", onVIPRequestStatusUpdate);
    // User cam events
    communicator.value.on("userCamStatus", onUserCamStatus);
    communicator.value.on("disconnected", onDisconnected);
  };

  const activateUserCam = () => {
    communicator.value?.activateUserCam();
  };

  const deactivateUserCam = () => {
    communicator.value?.deactivateUserCam();
  };

  const sendMessage = (message: string) => {
    communicator.value?.sendMessage(message);
  };

  const setVolume = (volume: number) => {
    if (volume >= 0 && volume <= 1) {
      communicator.value?.setVolume(volume);
    }
  };

  const setMuted = (muted: boolean) => {
    communicator.value?.setMuted(muted);
  };

  const goToGroupChat = () => {
    communicator.value?.goToGroupChat();
  };

  const requestVIP = () => {
    communicator.value?.requestVIP();
  };

  initCommunicator();

  return {
    communicator,
    state,
    setVolume,
    setMuted,
    activateUserCam,
    deactivateUserCam,
    sendMessage,
    goToGroupChat,
    requestVIP,
    cleanup,
  };
}
